import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ListOrdered, Plus, RefreshCw } from "lucide-react";
import { toast } from "react-toastify";
import { Button } from "../components/ui/Button";
import { SunbedActionToolbar } from "../components/sunbeds/SunbedActionToolbar";
import { PaginationFooter } from "../components/sunbeds/PaginationFooter";
import { SunbedWaitingListTable } from "../components/sunbeds/SunbedWaitingListTable";
import { ConfirmationModal } from "../components/ui/ConfirmationModal";
import { useAuth } from "../auth/AuthContext";
import { useSunbedSessions } from "../context/SunbedSessionsContext";
import { hasSunbedWaitingListEnabled } from "../lib/sunbedWaitingList";
import { StartWaitingListModal } from "../components/sunbeds/StartWaitingListModal";
import { useToast } from "../hooks/useToast";
import { checkTmaxHealth, startTmaxSession } from "../services/tmax";
import {
  cancelSunbedWaitingListEntry,
  getSunbedWaitingList,
  getSunbedWaitingListErrorMessage,
  startSunbedWaitingListEntry,
  type SunbedWaitingListItem,
} from "../services/sunbedWaitingList";

const WAITING_LIST_POLL_MS = 60_000;

const SunbedWaitingListPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const showToast = useToast();
  const { refreshSessions } = useSunbedSessions();
  const waitingListEnabled = hasSunbedWaitingListEnabled(user);

  const [items, setItems] = useState<SunbedWaitingListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [actionLoadingId, setActionLoadingId] = useState<number | null>(null);
  const [cancelTarget, setCancelTarget] = useState<SunbedWaitingListItem | null>(null);
  const [startTarget, setStartTarget] = useState<SunbedWaitingListItem | null>(null);

  useEffect(() => {
    if (!waitingListEnabled) {
      navigate("/sunbed-session", { replace: true });
    }
  }, [waitingListEnabled, navigate]);

  const loadWaitingList = useCallback(async (opts?: { silent?: boolean }) => {
    const silent = Boolean(opts?.silent);
    if (!silent) setLoading(true);
    try {
      const rows = await getSunbedWaitingList();
      setItems(rows.filter((r) => String(r.status).toLowerCase() === "waiting"));
    } catch (err) {
      console.error("Failed to load waiting list", err);
      if (!silent) {
        toast.error(getSunbedWaitingListErrorMessage(err));
        setItems([]);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!waitingListEnabled) return;
    void loadWaitingList();
  }, [waitingListEnabled, loadWaitingList]);

  useEffect(() => {
    if (!waitingListEnabled) return;
    const id = window.setInterval(() => {
      void loadWaitingList({ silent: true });
    }, WAITING_LIST_POLL_MS);
    return () => window.clearInterval(id);
  }, [waitingListEnabled, loadWaitingList]);

  const filtered = items.filter((item) => {
    const q = searchTerm.toLowerCase().trim();
    if (!q) return true;
    return (
      item.customer.toLowerCase().includes(q) ||
      item.package_name.toLowerCase().includes(q) ||
      (item.customer_phone?.toLowerCase().includes(q) ?? false)
    );
  });

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const paginated = filtered.slice(indexOfFirstItem, indexOfLastItem);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const handleCancelConfirm = async () => {
    if (!cancelTarget) return;
    setActionLoadingId(cancelTarget.id);
    try {
      await cancelSunbedWaitingListEntry(cancelTarget.id);
      toast.success("Removed from waiting list.");
      setCancelTarget(null);
      await loadWaitingList({ silent: true });
    } catch (err) {
      toast.error(getSunbedWaitingListErrorMessage(err));
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleStart = (item: SunbedWaitingListItem) => {
    setStartTarget(item);
  };

  const handleStartConfirm = async (roomId: number, tmaxId?: number) => {
    if (!startTarget || !roomId) return;
    setActionLoadingId(startTarget.id);
    try {
      const isTmaxEnabled = user?.company?.is_sunbed_tmax;
      if (isTmaxEnabled) {
        try {
          const health = await checkTmaxHealth();
          if (!health?.ok) {
            showToast.error("The third-party service is not available at the moment.");
            return;
          }
        } catch {
          showToast.error("The third-party service is not available at the moment.");
          return;
        }
        if (!tmaxId) {
          showToast.warning("TMAX ID is missing for the selected room.");
          return;
        }
        try {
          await startTmaxSession(
            tmaxId,
            startTarget.duration_minutes,
            startTarget.delay_minutes ?? 0
          );
        } catch (err) {
          console.error("Failed to start TMAX session from waiting list", err);
          showToast.error("Failed to start the session with the third-party service.");
          return;
        }
      }

      const result = await startSunbedWaitingListEntry(startTarget.id, { room_id: roomId });
      showToast.success(result.message || "Session started successfully.");
      setStartTarget(null);
      await refreshSessions();
      navigate("/sunbed-session");
    } catch (err) {
      showToast.error(getSunbedWaitingListErrorMessage(err));
    } finally {
      setActionLoadingId(null);
    }
  };

  if (!waitingListEnabled) {
    return null;
  }

  return (
    <div>
      <div className="px-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between px-6 py-6 bg-white border-b border-gray-200">
          <div className="flex items-center gap-4 min-w-0">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="shrink-0 text-gray-600 hover:bg-purple-50"
              onClick={() => navigate("/sunbed-session")}
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden sm:inline">Sessions</span>
            </Button>
            <div className="bg-[#F3E8FF] p-3 rounded-xl shrink-0">
              <ListOrdered className="h-6 w-6 text-[#885ABB]" />
            </div>
            <div className="min-w-0">
              <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Waiting List</h1>
              <p className="text-sm text-gray-500 font-medium">
                Customers queued while all sunbed rooms are in use or cooling
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 shrink-0">
            <Button
              type="button"
              variant="outline"
              className="gap-2 border-gray-200"
              disabled={loading}
              onClick={() => loadWaitingList()}
            >
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button
              type="button"
              className="bg-[#885ABB] hover:bg-[#7748A4] text-white gap-2 shadow-sm"
              onClick={() => navigate("/sunbed-session")}
            >
              <Plus size={18} />
              New Session
            </Button>
          </div>
        </div>
      </div>

      <div className="flex-1 flex flex-col p-6 space-y-6 overflow-hidden">
        <SunbedActionToolbar
          totalItems={items.length}
          showingStart={paginated.length > 0 ? indexOfFirstItem + 1 : 0}
          showingEnd={Math.min(indexOfLastItem, filtered.length)}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          searchPlaceholder="Search customer or package..."
        />

        <div className="flex-1 rounded-xl border border-gray-200 shadow-sm bg-white overflow-hidden flex flex-col min-h-[320px]">
          <SunbedWaitingListTable
            items={paginated}
            loading={loading}
            indexOfFirstItem={indexOfFirstItem}
            onCancel={(id) => {
              const row = items.find((i) => i.id === id);
              if (row) setCancelTarget(row);
            }}
            onStart={handleStart}
            actionLoadingId={actionLoadingId}
          />

          {!loading && filtered.length > itemsPerPage && (
            <PaginationFooter
              totalItems={items.length}
              filteredTotal={filtered.length}
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
            />
          )}
        </div>
      </div>

      <ConfirmationModal
        isOpen={Boolean(cancelTarget)}
        onClose={() => setCancelTarget(null)}
        onConfirm={handleCancelConfirm}
        title="Remove from waiting list"
        message={
          cancelTarget
            ? `Remove ${cancelTarget.customer} from the waiting list?`
            : ""
        }
        variant="warning"
        confirmLabel="Remove"
      />

      <StartWaitingListModal
        isOpen={Boolean(startTarget)}
        item={startTarget}
        loading={startTarget != null && actionLoadingId === startTarget.id}
        onClose={() => setStartTarget(null)}
        onConfirm={handleStartConfirm}
      />
    </div>
  );
};

export default SunbedWaitingListPage;
